/**
 * TrackRing — THE component of v2. Ported from the OnFlow Design System
 * project (v2/components/track/TrackRing.jsx).
 *
 * Two concentric arcs answer "am I on track" in one glance:
 *   outer = how far you've come  (coloured by state)
 *   inner = how much of the window is gone (always dim)
 *
 * If the dim inner arc is ahead of the coloured outer arc, time is running out
 * faster than progress is arriving. That relationship is the whole insight and
 * it needs no reading.
 */
import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import type { TrackState } from './FlowStatus.js';

const C: Record<Exclude<TrackState, 'neutral'>, string> = {
  'on-track': 'var(--on-track)',
  drifting: 'var(--drifting)',
  'off-track': 'var(--off-track)',
};

/**
 * Derive the verdict from the two arcs. The colour must never be able to
 * disagree with the geometry — that is the one bug this component can have.
 */
export function trackStateFor(progress = 0, elapsed = 0): Exclude<TrackState, 'neutral'> {
  if (progress >= elapsed - 5) return 'on-track';
  if (progress >= elapsed - 15) return 'drifting';
  return 'off-track';
}

interface TrackRingProps {
  /** Outer arc, 0-100. */
  progress?: number;
  /** Inner arc, 0-100. */
  elapsed?: number;
  /** Overrides the derived verdict. Omit to let the geometry decide. */
  state?: Exclude<TrackState, 'neutral'>;
  size?: number;
  label?: string;
  value?: ReactNode;
  suffix?: string;
  animate?: boolean;
  style?: CSSProperties;
}

export function TrackRing({
  progress = 0,
  elapsed = 0,
  state,
  size = 208,
  label,
  value,
  suffix,
  animate = true,
  style,
}: TrackRingProps) {
  const stroke = Math.round(size * 0.058);
  const gap = Math.round(size * 0.052);
  const rOuter = (size - stroke) / 2;
  const rInner = rOuter - stroke / 2 - gap;
  const cOuter = 2 * Math.PI * rOuter;
  const cInner = 2 * Math.PI * rInner;
  const resolved = state ?? trackStateFor(progress, elapsed);
  const accent = C[resolved];

  // Arcs sweep in from zero on mount so the number and the geometry land
  // together. Not a loop, not scroll-triggered — one sweep per value change.
  const [p, setP] = useState(animate ? 0 : progress);
  const [e, setE] = useState(animate ? 0 : elapsed);
  useEffect(() => {
    if (!animate) {
      setP(progress);
      setE(elapsed);
      return;
    }
    const t = window.setTimeout(() => {
      setP(progress);
      setE(elapsed);
    }, 60);
    return () => window.clearTimeout(t);
  }, [progress, elapsed, animate]);

  const clamp = (n: number) => Math.max(0, Math.min(100, n));

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0, ...style }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}
        aria-hidden
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={rOuter}
          fill="none"
          stroke="var(--paper-3)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={rOuter}
          fill="none"
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={cOuter}
          strokeDashoffset={cOuter * (1 - clamp(p) / 100)}
          style={{
            transition: 'stroke-dashoffset var(--t-arc) var(--ease-out)',
            filter: `drop-shadow(0 0 10px color-mix(in srgb, ${accent} 45%, transparent))`,
          }}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={rInner}
          fill="none"
          stroke="var(--paper-2)"
          strokeWidth={stroke * 0.5}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={rInner}
          fill="none"
          stroke="var(--ink-3)"
          strokeWidth={stroke * 0.5}
          strokeLinecap="round"
          strokeDasharray={cInner}
          strokeDashoffset={cInner * (1 - clamp(e) / 100)}
          style={{ transition: 'stroke-dashoffset var(--t-arc) var(--ease-out)' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          textAlign: 'center',
          padding: '0 14%',
        }}
      >
        {label && (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-label-size)',
              letterSpacing: 'var(--text-label-tracking)',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
            }}
          >
            {label}
          </span>
        )}
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: Math.round(size * 0.24),
            fontWeight: 700,
            letterSpacing: '-0.04em',
            lineHeight: 0.92,
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--ink-1)',
          }}
        >
          {value}
        </span>
        {suffix && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)' }}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

interface TrackRingLegendProps {
  state?: Exclude<TrackState, 'neutral'>;
  progressLabel?: string;
  elapsedLabel?: string;
  style?: CSSProperties;
}

/** The legend that makes the two arcs legible the first time you see them. */
export function TrackRingLegend({
  state = 'on-track',
  progressLabel = 'Goal progress',
  elapsedLabel = 'Time elapsed',
  style,
}: TrackRingLegendProps) {
  const accent = C[state];
  const item = (c: string, t: string, thin: boolean) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <span
        style={{ width: 14, height: thin ? 3 : 6, borderRadius: 999, background: c, flexShrink: 0 }}
      />
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
        }}
      >
        {t}
      </span>
    </span>
  );
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', ...style }}>
      {item(accent, progressLabel, false)}
      {item('var(--ink-3)', elapsedLabel, true)}
    </div>
  );
}
